/**
 * T558 — a `<Link>` that says it heard the tap.
 *
 * `<Link>` plus the barrido, in one place. It exists so that applying the idiom to a
 * control is a rename and not a copy of "compare `navigation.location.pathname` to my
 * `to`": every hand-written copy of that comparison is another chance to write it
 * slightly differently, and the disagreement would show on screen as two lit controls
 * (R-T556-3, restated as R-T558-3). It is also what lets a mapped list — the account tabs,
 * the per-day session links — give each item its own pending state without calling a hook
 * inside a loop.
 *
 * The rule itself lives in `~/lib/nav-pending`; this only wires it to an element.
 */
import { Link, type LinkProps } from "react-router";
import { cn } from "~/lib/cn";
import { startsInAppNavigation, useNavPendingTo } from "~/components/layout/use-nav-pending";
import { NavPendingSweep } from "./NavPendingSweep";

export function NavPendingLink({ to, className, children, onClick, ...rest }: LinkProps) {
  // A `To` object carries no path this predicate can compare, so it stays dark rather than
  // guessing. Every member control that uses this passes a string.
  const { pending, onNavigate } = useNavPendingTo(typeof to === "string" ? to : null);
  return (
    <Link
      to={to}
      className={cn(className, pending && "nav-pending")}
      /* R-T558-5 — `aria-busy`, never an early `aria-current`. The second would tell a
         screen reader she has arrived somewhere she has not. */
      aria-busy={pending || undefined}
      /* T560 — the tap is what makes this control the one that lights. T972: the stamp
         runs AFTER the caller's handler, and only when the click still starts an in-app
         navigation — a caller that prevents default (opening a layer instead) has
         cancelled the navigation, so it must not leave a live stamp. A tap while already
         pending is refused. */
      onClick={(e) => {
        if (pending && startsInAppNavigation(e)) { e.preventDefault(); return; }
        onClick?.(e);
        if (startsInAppNavigation(e)) onNavigate();
      }}
      {...rest}
    >
      {children}
      {pending ? <NavPendingSweep /> : null}
    </Link>
  );
}
